import { DeviceApiError } from '../api/device-api.js';
import { PLAYBACK_BATCH_MAX_ATTEMPTS } from './playback-event-buffer.js';
/** Up to 5 batches (500 events) per sync, so a backlog after offline time clears quickly. */
const MAX_BATCHES_PER_SYNC = 5;
/**
 * Whether a failed send counts towards setting the batch aside: network errors, 5xx, and
 * 400/413/422 (the batch can never succeed). 401/403 (credentials) and 429 (rate limit)
 * don't count, because the batch itself isn't the problem.
 */
function countsAsBatchFailure(error) {
    if (!(error instanceof DeviceApiError))
        return true;
    return error.status >= 500 || error.status === 400 || error.status === 413 || error.status === 422;
}
/**
 * Sends queued playback events, plus the queue-loss count when events were dropped.
 * Events are removed only after the server answers OK with per-event rejections.
 * A failed request keeps the batch for the next sync, unless the same batch has failed
 * PLAYBACK_BATCH_MAX_ATTEMPTS times in a row: then it is set aside and counted as dropped.
 * Returns how many events left the queue after a successful send.
 */
export async function sendPlaybackEvents(input) {
    const report = input.api.reportPlaybackEvents?.bind(input.api);
    if (!report)
        return 0;
    let sent = 0;
    for (let i = 0; i < MAX_BATCHES_PER_SYNC; i += 1) {
        const batch = input.buffer.takeBatch();
        const queueLoss = input.buffer.pendingQueueLoss();
        if (batch.length === 0 && !queueLoss)
            break;
        const ids = batch.map((e) => e.clientEventId);
        try {
            const result = await report(input.token, queueLoss ? { events: batch, queueLoss } : { events: batch });
            const rejected = ids.filter((id) => result.rejectedClientEventIds?.includes(id));
            if (rejected.length > 0) {
                input.logger.warn('Playback events rejected individually by the server', {
                    count: rejected.length,
                    clientEventIds: rejected,
                });
            }
            // Successful response: valid events were handled, including duplicates.
            // Explicitly rejected events are discarded, not retried.
            input.buffer.ack(ids);
            if (result.queueLossAck)
                input.buffer.acknowledgeQueueLoss(result.queueLossAck);
            sent += batch.length;
            if (batch.length === 0)
                break; // loss-only report, nothing else to send
        }
        catch (error) {
            const status = error instanceof DeviceApiError ? error.status : undefined;
            const message = error instanceof Error ? error.message : String(error);
            if (batch.length > 0 && countsAsBatchFailure(error) && input.buffer.recordBatchFailure(batch)) {
                input.logger.warn('Playback batch set aside after repeated failures, counted as dropped', {
                    status,
                    count: batch.length,
                    attempts: PLAYBACK_BATCH_MAX_ATTEMPTS,
                    error: message,
                });
                continue;
            }
            input.logger.warn('Playback events not sent — will retry next sync', { status, error: message });
            break;
        }
    }
    return sent;
}
//# sourceMappingURL=send-playback-events.js.map